/**
 * Given a leader option and its follower part, finds the matching follower option.
 *
 * Matching is attempted in two steps:
 * 1. Exact suffix match — strips the part-type prefix from the leader SKU and finds
 *    the follower option whose SKU ends with the same suffix (e.g. `_Ipink`).
 * 2. Bare-colour fallback — strips the material character (F/I/M) from the suffix and
 *    matches against the follower option's skuWithoutPrefix, also stripped. This handles
 *    cases where the follower part only stocks one material variant.
 *
 * @param {Object} leaderOption - A PartOption instance for the leader (e.g. frame)
 * @param {Object} followerPart - A Part instance whose followerOf matches the leader part's skuPrefix
 * @returns {Object|null} The matching follower PartOption, or null if not found
 */
export function findFollowerOption(leaderOption, followerPart) {
  const leaderSKUSuffix = leaderOption.sku.split('_').slice(1).join('_')

  let followerOption = followerPart.options.find((o) => o.sku.endsWith(`_${leaderSKUSuffix}`))

  if (!followerOption) {
    const bareColour = leaderSKUSuffix.replace(/^[FIM]/, '')
    followerOption = followerPart.options.find(
      (o) => o.skuWithoutPrefix?.replace(/^[FIM]/, '') === bareColour
    )
  }

  return followerOption || null
}

/**
 * Given a product and a part, finds the part that follows it (i.e. whose followerOf
 * matches the given part's skuPrefix).
 *
 * @param {Object} product - The product instance with a parts array
 * @param {Object} part - The leader Part instance
 * @returns {Object|null} The follower Part instance, or null if none exists
 */
export function findFollowerPart(product, part) {
  return product.parts.find((p) => p.followerOf === part.skuPrefix) || null
}
